import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { fetchOrders, updateOrderStatus, confirmOrder, rejectOrder, completeOrder, returnToAwaitingConfirmation } from '../services/api';
import { connectSSE, disconnectSSE, sseService } from '../services/sse';
import {
  preloadNotificationSounds,
  playNewOrderSound,
  playTimeToPrepareSound,
  // Системные алерты браузера отключены — мешают звуку в киоске
  // requestNotificationPermission,
  // showNewOrderNotification,
  // showTimeToPrepareNotification,
} from '../services/notifications';
import { parseUTCTime } from '../utils/timeUtils';
import { useAuth } from './AuthContext';

const OrdersContext = createContext(null);

/** Статусы, для которых имеет смысл напоминание «пора готовить» до estimated_ready_time */
const PREPARE_STATUSES = ['paid', 'confirmed', 'preparing'];

const FIVE_MIN_MS = 5 * 60 * 1000;

export const OrdersProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newOrdersQueue, setNewOrdersQueue] = useState([]);
  /** SSE: null | { status: 'reconnecting', attempt, maxAttempts } | { status: 'reconnect_failed' } */
  const [connectionStatus, setConnectionStatus] = useState(null);
  // Храним предыдущие статусы заказов для определения переходов
  const previousOrderStatusesRef = useRef(new Map());
  // Заказы, по которым уже сыграл сигнал «пора готовить» (за 5 мин)
  const timeToPrepareNotifiedIdsRef = useRef(new Set());
  const ordersRef = useRef(orders);
  ordersRef.current = orders;

  /**
   * Transform order data from backend format to display format
   * Ensures all items have properly formatted names for display
   */
  const transformOrderData = useCallback((order) => {
    if (!order) {
      console.warn('[OrdersContext] transformOrderData: No order provided');
      return null;
    }

    // If items is missing or empty, return order with empty items array
    if (!order.items || !Array.isArray(order.items) || order.items.length === 0) {
      console.warn('[OrdersContext] transformOrderData: No items in order', {
        orderId: order.id,
        hasItems: !!order.items,
        isArray: Array.isArray(order.items),
        length: order.items?.length,
        order: order
      });
      return {
        ...order,
        items: []
      };
    }

    console.log('[OrdersContext] transformOrderData: Input order:', JSON.stringify(order, null, 2));

    const transformedItems = order.items.map((item, index) => {
      console.log(`[OrdersContext] Processing item ${index}:`, JSON.stringify(item, null, 2));
      
      // Extract item name from various possible locations
      let itemName = 'Позиция';
      
      if (item.item_name) {
        itemName = item.item_name;
        console.log(`[OrdersContext] Item ${index}: Found item_name:`, itemName);
      } else if (item.menu_item && item.menu_item.name) {
        itemName = item.menu_item.name;
        console.log(`[OrdersContext] Item ${index}: Found menu_item.name:`, itemName);
      } else {
        console.warn(`[OrdersContext] Item ${index}: No name found, using default`);
      }
      
      // Extract size name
      let sizeName = '';
      if (item.size_name) {
        sizeName = item.size_name;
        console.log(`[OrdersContext] Item ${index}: Found size_name:`, sizeName);
      } else if (item.menu_item && item.menu_item.sizes && item.menu_item.sizes.length > 0) {
        sizeName = item.menu_item.sizes[0].size_name || '';
        console.log(`[OrdersContext] Item ${index}: Found menu_item.sizes[0].size_name:`, sizeName);
      }
      
      // Extract addons: API присылает item.addons (OrderItemAddon: addon_id, addon_name, price_at_time).
      // Fallback: menu_item.addons теперь сгруппированы: [{ type, positions: [Addon] }].
      const addons = [];
      if (item.addons && Array.isArray(item.addons)) {
        item.addons.forEach(addon => {
          addons.push({
            name: addon.addon_name ?? addon.name,
            addon_name: addon.addon_name ?? addon.name,
            price_at_time: addon.price_at_time ?? addon.price,
            id: addon.id,
            addon_id: addon.addon_id ?? addon.id,
          });
        });
        console.log(`[OrdersContext] Item ${index}: Found ${addons.length} addons in item.addons`);
      } else if (item.menu_item && item.menu_item.addons && Array.isArray(item.menu_item.addons)) {
        // API: addons сгруппированы по типу — [{ type: "syrup"|"milk", positions: [...] }]
        const flatAddons = item.menu_item.addons.flatMap((group) => group.positions ?? []);
        flatAddons.forEach((addon) => {
          if (addon.name || addon.addon_name) {
            addons.push({
              name: addon.name ?? addon.addon_name,
              addon_name: addon.addon_name ?? addon.name,
              price_at_time: addon.price_at_time ?? addon.price ?? 0,
              id: addon.id,
              addon_id: addon.addon_id ?? addon.id,
            });
          }
        });
        console.log(`[OrdersContext] Item ${index}: Found ${addons.length} addons in menu_item.addons (grouped)`);
      }

      const transformedItem = {
        ...item,
        item_name: itemName,
        size_name: sizeName,
        addons: addons,
        // Keep original nested data for reference
        menu_item: item.menu_item,
      };

      console.log(`[OrdersContext] Item ${index} transformed:`, JSON.stringify(transformedItem, null, 2));
      
      return transformedItem;
    });

    const transformedOrder = {
      ...order,
      items: transformedItems,
    };

    console.log('[OrdersContext] transformOrderData: Output order:', JSON.stringify(transformedOrder, null, 2));

    return transformedOrder;
  }, []);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      // Получаем текущую дату в формате YYYY-MM-DD
      const today = new Date().toISOString().split('T')[0];
      console.log('[OrdersContext] loadOrders: Fetching orders for date:', today);
      const data = await fetchOrders(today, today);
      console.log('[OrdersContext] loadOrders: Raw API response:', JSON.stringify(data, null, 2));
      
      const transformedOrders = Array.isArray(data) 
        ? data.map(transformOrderData)
        : [];
      console.log('[OrdersContext] loadOrders: Transformed orders count:', transformedOrders.length);
      
      // Сохраняем статусы заказов в ref перед обновлением состояния
      transformedOrders.forEach(order => {
        previousOrderStatusesRef.current.set(order.id, order.status);
      });
      
      setOrders(transformedOrders);
      
      // Добавляем заказы со статусом pending или new в очередь уведомлений при начальной загрузке
      const pendingOrders = transformedOrders.filter(order => 
        order.status === 'pending' || order.status === 'new'
      );
      if (pendingOrders.length > 0) {
        setNewOrdersQueue(prevQueue => {
          // Добавляем только те заказы, которых еще нет в очереди
          const existingIds = new Set(prevQueue.map(o => o.id));
          const newOrders = pendingOrders.filter(o => !existingIds.has(o.id));
          if (newOrders.length > 0) {
            console.log(`[OrdersContext] При начальной загрузке добавлено ${newOrders.length} заказов в очередь уведомлений`);
            return [...prevQueue, ...newOrders];
          }
          return prevQueue;
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки заказов:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSSEEvent = useCallback((eventType, eventData) => {
    console.log('Получено SSE событие:', eventType, eventData);
    
    if (eventType === 'order.created') {
      const newOrder = transformOrderData(eventData.data);
      console.log('Получен новый заказ (трансформированный):', newOrder);
      
      // Сохраняем статус нового заказа в ref
      previousOrderStatusesRef.current.set(newOrder.id, newOrder.status);
      
      // Добавляем заказ в список
      setOrders(prevOrders => {
        // Проверяем, нет ли уже такого заказа
        const exists = prevOrders.some(o => o.id === newOrder.id);
        if (exists) {
          return prevOrders;
        }
        return [newOrder, ...prevOrders];
      });
      
      // Добавляем заказ в очередь уведомлений
      setNewOrdersQueue(prevQueue => {
        // Проверяем, нет ли уже такого заказа в очереди
        const exists = prevQueue.some(o => o.id === newOrder.id);
        if (exists) {
          return prevQueue;
        }
        console.log(`[OrdersContext] Добавлен заказ #${newOrder.id} в очередь. Позиция в очереди: ${prevQueue.length + 1}`);
        return [...prevQueue, newOrder];
      });

      // Системный алерт отключён — только звук
      // showNewOrderNotification(newOrder);
      playNewOrderSound();
    } else if (eventType === 'order.status_updated') {
      const { order } = eventData.data;
      const transformedOrder = transformOrderData(order);
      console.log('[OrdersContext] Обновлен статус заказа (трансформированный):', transformedOrder);
      
      // Получаем предыдущий статус из ref
      const previousStatus = previousOrderStatusesRef.current.get(transformedOrder.id) || null;
      console.log(`[OrdersContext] Заказ #${transformedOrder.id}: предыдущий статус из ref = ${previousStatus}, новый статус = ${transformedOrder.status}`);
      
      // Обновляем заказ в списке (отмененные заказы остаются для истории)
      setOrders(prevOrders => {
        const existingOrder = prevOrders.find(o => o.id === transformedOrder.id);
        const currentStatus = existingOrder ? existingOrder.status : null;
        
        if (currentStatus && currentStatus !== transformedOrder.status) {
          console.log(`[OrdersContext] Заказ #${transformedOrder.id} изменил статус в списке: ${currentStatus} -> ${transformedOrder.status}`);
        }
        
        // Сохраняем новый статус в ref
        previousOrderStatusesRef.current.set(transformedOrder.id, transformedOrder.status);
        
        const exists = prevOrders.some(o => o.id === transformedOrder.id);
        if (exists) {
          // Обновляем заказ в списке (даже если отменен - для истории)
          return prevOrders.map(o =>
            o.id === transformedOrder.id ? transformedOrder : o
          );
        }
        return [transformedOrder, ...prevOrders];
      });
      
      // Если заказ вернулся в статус pending или new, добавляем его в очередь уведомлений
      const isPendingOrNew = transformedOrder.status === 'pending' || transformedOrder.status === 'new';
      const wasAwaitingConfirmation = previousStatus === 'awaiting_confirmation';
      const isCancelled = transformedOrder.status === 'cancelled';
      
      // Если заказ отменен - УДАЛЯЕМ его из очереди уведомлений
      if (isCancelled) {
        setNewOrdersQueue(prevQueue => {
          const exists = prevQueue.some(o => o.id === transformedOrder.id);
          if (exists) {
            const newQueue = prevQueue.filter(o => o.id !== transformedOrder.id);
            console.log(`[OrdersContext] Заказ #${transformedOrder.id} отменен и удален из очереди уведомлений. Осталось в очереди: ${newQueue.length}`);
            return newQueue;
          }
          return prevQueue;
        });
      } else if (isPendingOrNew) {
        setNewOrdersQueue(prevQueue => {
          // Проверяем, нет ли уже такого заказа в очереди
          const existsInQueue = prevQueue.some(o => o.id === transformedOrder.id);
          
          // Если заказ был в awaiting_confirmation и вернулся в pending/new,
          // ОБЯЗАТЕЛЬНО добавляем его в очередь
          // Также добавляем, если его еще нет в очереди
          if (wasAwaitingConfirmation) {
            // Заказ вернулся из awaiting_confirmation - обязательно добавляем/обновляем
            if (existsInQueue) {
              console.log(`[OrdersContext] Заказ #${transformedOrder.id} вернулся в статус ${transformedOrder.status} после редактирования (был ${previousStatus}), обновлен в очереди уведомлений`);
              return prevQueue.map(o =>
                o.id === transformedOrder.id ? transformedOrder : o
              );
            } else {
              console.log(`[OrdersContext] Заказ #${transformedOrder.id} вернулся в статус ${transformedOrder.status} после редактирования (был ${previousStatus}), добавлен в очередь уведомлений. Позиция в очереди: ${prevQueue.length + 1}`);
              return [...prevQueue, transformedOrder];
            }
          } else if (!existsInQueue) {
            // Заказ не был в awaiting_confirmation, но его нет в очереди - добавляем
            console.log(`[OrdersContext] Заказ #${transformedOrder.id} со статусом ${transformedOrder.status} (был ${previousStatus}) добавлен в очередь уведомлений. Позиция в очереди: ${prevQueue.length + 1}`);
            return [...prevQueue, transformedOrder];
          } else {
            // Заказ уже в очереди и не был в awaiting_confirmation, просто обновляем его данные
            console.log(`[OrdersContext] Заказ #${transformedOrder.id} уже в очереди, обновляем данные (статус: ${transformedOrder.status}, был: ${previousStatus})`);
            return prevQueue.map(o =>
              o.id === transformedOrder.id ? transformedOrder : o
            );
          }
        });
      } else {
        // Для других статусов просто обновляем заказ в очереди, если он там есть
        setNewOrdersQueue(prevQueue => {
          const exists = prevQueue.some(o => o.id === transformedOrder.id);
          if (exists) {
            console.log(`[OrdersContext] Заказ #${transformedOrder.id} обновлен в очереди (статус: ${transformedOrder.status})`);
            return prevQueue.map(o =>
              o.id === transformedOrder.id ? transformedOrder : o
            );
          }
          return prevQueue;
        });
      }
    } else if (eventType === 'connected') {
      console.log('SSE подключен:', eventData);
    }
  }, [transformOrderData]);

  // Загрузка заказов только после успешной аутентификации
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadOrders();
    } else if (!authLoading && !isAuthenticated) {
      // Очищаем заказы при выходе из системы
      setOrders([]);
      setNewOrdersQueue([]);
      timeToPrepareNotifiedIdsRef.current = new Set();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading]);

  // Подключение к SSE только после успешной аутентификации
  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      if (!authLoading && !isAuthenticated) {
        disconnectSSE();
        setConnectionStatus(null);
      }
      return;
    }

    let reconnectedTimeoutId = null;

    const onConnectionStatus = (payload) => {
      if (payload.status === 'connected') {
        setConnectionStatus(null);
      } else if (payload.status === 'reconnected') {
        setConnectionStatus({ status: 'reconnected' });
        if (reconnectedTimeoutId) clearTimeout(reconnectedTimeoutId);
        reconnectedTimeoutId = setTimeout(() => {
          reconnectedTimeoutId = null;
          setConnectionStatus(null);
        }, 5000);
      } else if (payload.status === 'reconnecting') {
        setConnectionStatus({ status: 'reconnecting', attempt: payload.attempt, maxAttempts: payload.maxAttempts });
      } else if (payload.status === 'reconnect_failed') {
        setConnectionStatus({ status: 'reconnect_failed' });
      }
    };

    sseService.on('connection_status', onConnectionStatus);
    console.log('[OrdersContext] Connecting to SSE after successful authentication');
    connectSSE(handleSSEEvent);

    return () => {
      if (reconnectedTimeoutId) clearTimeout(reconnectedTimeoutId);
      sseService.off('connection_status', onConnectionStatus);
      console.log('[OrdersContext] Disconnecting from SSE');
      disconnectSSE();
      setConnectionStatus(null);
    };
  }, [isAuthenticated, authLoading, handleSSEEvent]);

  // Предзагрузка звуков после входа (системные Notification отключены)
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      // requestNotificationPermission();
      preloadNotificationSounds();
    }
  }, [isAuthenticated, authLoading]);

  const checkTimeToPrepare = useCallback(() => {
    if (!isAuthenticated || authLoading) return;

    const currentOrders = ordersRef.current;
    const now = Date.now();

    currentOrders.forEach((order) => {
      const etaRaw = order.estimated_ready_time ?? order.estimatedReadyTime;
      if (!etaRaw || !PREPARE_STATUSES.includes(order.status)) return;
      if (timeToPrepareNotifiedIdsRef.current.has(order.id)) return;

      const readyTime = parseUTCTime(etaRaw);
      if (!readyTime || Number.isNaN(readyTime.getTime())) return;

      const prepareBy = readyTime.getTime() - FIVE_MIN_MS;
      if (now >= prepareBy) {
        timeToPrepareNotifiedIdsRef.current.add(order.id);
        playTimeToPrepareSound();
        // showTimeToPrepareNotification(order);
        console.log(`[OrdersContext] Оповещение «пора готовить» для заказа #${order.id}`);
      }
    });
  }, [isAuthenticated, authLoading]);

  // Сразу при появлении/обновлении заказов (раньше проверяли только раз в 30 с — часто пропускали окно)
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    checkTimeToPrepare();
  }, [isAuthenticated, authLoading, orders, checkTimeToPrepare]);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    const intervalId = setInterval(checkTimeToPrepare, 15 * 1000);
    return () => clearInterval(intervalId);
  }, [isAuthenticated, authLoading, checkTimeToPrepare]);

  const handleConfirmOrder = async (orderId) => {
    try {
      const updatedOrder = await confirmOrder(orderId);
      const transformedOrder = transformOrderData(updatedOrder);
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? transformedOrder : order
        )
      );
      // Удаляем заказ из очереди уведомлений
      setNewOrdersQueue(prevQueue => {
        const newQueue = prevQueue.filter(order => order.id !== orderId);
        console.log(`[OrdersContext] Заказ #${orderId} удален из очереди. Осталось в очереди: ${newQueue.length}`);
        return newQueue;
      });
    } catch (error) {
      console.error('Ошибка подтверждения заказа:', error);
      throw error;
    }
  };

  const handleRejectOrder = async (orderId) => {
    try {
      await rejectOrder(orderId);
      setOrders(prevOrders =>
        prevOrders.filter(order => order.id !== orderId)
      );
      // Удаляем заказ из очереди уведомлений
      setNewOrdersQueue(prevQueue => {
        const newQueue = prevQueue.filter(order => order.id !== orderId);
        console.log(`[OrdersContext] Заказ #${orderId} удален из очереди. Осталось в очереди: ${newQueue.length}`);
        return newQueue;
      });
    } catch (error) {
      console.error('Ошибка отклонения заказа:', error);
      throw error;
    }
  };

  const handleCompleteOrder = async (orderId) => {
    try {
      const updatedOrder = await completeOrder(orderId);
      const transformedOrder = transformOrderData(updatedOrder);
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? transformedOrder : order
        )
      );
    } catch (error) {
      console.error('Ошибка завершения заказа:', error);
      throw error;
    }
  };

  const handleReturnToAwaitingConfirmation = async (orderId, reason = null) => {
    try {
      const updatedOrder = await returnToAwaitingConfirmation(orderId, reason);
      const transformedOrder = transformOrderData(updatedOrder);
      
      // Обновляем статус в ref
      previousOrderStatusesRef.current.set(orderId, transformedOrder.status);
      
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? transformedOrder : order
        )
      );
      // Удаляем заказ из очереди уведомлений
      setNewOrdersQueue(prevQueue => {
        const newQueue = prevQueue.filter(order => order.id !== orderId);
        console.log(`[OrdersContext] Заказ #${orderId} удален из очереди. Осталось в очереди: ${newQueue.length}`);
        return newQueue;
      });
      console.log(`[OrdersContext] Заказ #${orderId} возвращен в статус awaiting_confirmation${reason ? ' с комментарием' : ''}`);
    } catch (error) {
      console.error('Ошибка возврата заказа в ожидание:', error);
      throw error;
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const updatedOrder = await updateOrderStatus(orderId, newStatus);
      const transformedOrder = transformOrderData(updatedOrder);
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? transformedOrder : order
        )
      );
    } catch (error) {
      console.error('Ошибка обновления статуса заказа:', error);
      throw error;
    }
  };

  const dismissNewOrderNotification = () => {
    // Эта функция больше ничего не делает, так как удаление из очереди 
    // происходит в confirmOrder/rejectOrder/returnToAwaitingConfirmation
    // Оставлена для обратной совместимости
    console.log('[OrdersContext] dismissNewOrderNotification вызван (no-op)');
  };

  const getOrdersByStatus = (status) => {
    return orders.filter(order => order.status === status);
  };

  const value = {
    orders,
    isLoading,
    newOrderNotification: newOrdersQueue[0] || null, // Первый заказ в очереди
    newOrdersQueue, // Вся очередь для отображения счетчика
    connectionStatus, // { status: 'reconnecting'|'reconnect_failed', attempt?, maxAttempts? } | null
    confirmOrder: handleConfirmOrder,
    rejectOrder: handleRejectOrder,
    completeOrder: handleCompleteOrder,
    returnToAwaitingConfirmation: handleReturnToAwaitingConfirmation,
    updateOrderStatus: handleUpdateOrderStatus,
    dismissNewOrderNotification,
    getOrdersByStatus,
    refreshOrders: loadOrders
  };

  return (
    <OrdersContext.Provider value={value}>
      {children}
    </OrdersContext.Provider>
  );
};

export const useOrders = () => {
  const context = useContext(OrdersContext);
  if (!context) {
    throw new Error('useOrders должен использоваться внутри OrdersProvider');
  }
  return context;
};

